import { Fragment, type ReactNode } from "react";

/**
 * Minimal, safe Markdown → React (no innerHTML): inline `**bold**`, `*italic*` / `_italic_`,
 * `~~strike~~`, `` `code` ``, `[text](url)` and bare URLs. Block mode adds paragraphs,
 * `-`/`*`/`1.` lists, `#` headings and `> ` quotes. Only http(s)/mailto links are rendered.
 */

const INLINE =
  /(\*\*([^*]+)\*\*)|(~~([^~]+)~~)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))|(\*([^*\s][^*]*)\*)|(\b_([^_]+)_\b)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;

const safeHref = (url: string) => (/^(https?:|mailto:)/i.test(url) ? url : null);

export function renderInline(text: string, keyPrefix = "i", plainLinks = false): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (const m of text.matchAll(INLINE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const key = `${keyPrefix}${k++}`;
    if (m[1]) out.push(<strong key={key}>{renderInline(m[2] ?? "", key, plainLinks)}</strong>);
    else if (m[3]) out.push(<s key={key}>{renderInline(m[4] ?? "", key, plainLinks)}</s>);
    else if (m[5]) out.push(<code key={key}>{m[6]}</code>);
    else if (m[7]) {
      const href = safeHref(m[9] ?? "");
      out.push(
        href && !plainLinks ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            {m[8]}
          </a>
        ) : (
          <span key={key} className="md-link">
            {m[8]}
          </span>
        ),
      );
    } else if (m[10]) out.push(<em key={key}>{renderInline(m[11] ?? "", key, plainLinks)}</em>);
    else if (m[12]) out.push(<em key={key}>{renderInline(m[13] ?? "", key, plainLinks)}</em>);
    else if (m[14]) {
      out.push(
        plainLinks ? (
          <span key={key} className="md-link">
            {m[14]}
          </span>
        ) : (
          <a key={key} href={m[14]} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            {m[14]}
          </a>
        ),
      );
    }
    last = idx + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Plain text of a Markdown string (for previews and titles). */
export function stripMarkdown(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/(\*\*|~~|`)/g, "")
    .replace(/(^|\s)[*_]([^*_]+)[*_]/g, "$1$2");
}

export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.split("\n");
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (!line.trim()) {
      i++;
      continue;
    }
    const key = `b${k++}`;
    if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: ReactNode[] = [];
      while (i < lines.length && /^\s*([-*+]|\d+\.)\s+/.test(lines[i] ?? "")) {
        const body = (lines[i] ?? "").replace(/^\s*([-*+]|\d+\.)\s+/, "");
        items.push(<li key={items.length}>{renderInline(body, `${key}-${items.length}-`)}</li>);
        i++;
      }
      blocks.push(ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>);
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1]?.length ?? 1;
      const content = renderInline(heading[2] ?? "", key);
      blocks.push(level === 1 ? <h3 key={key}>{content}</h3> : <h4 key={key}>{content}</h4>);
      i++;
      continue;
    }
    if (line.startsWith(">")) {
      const quote: string[] = [];
      while (i < lines.length && (lines[i] ?? "").startsWith(">")) quote.push((lines[i] ?? "").replace(/^>\s?/, ""));
      i += quote.length;
      blocks.push(<blockquote key={key}>{renderInline(quote.join(" "), key)}</blockquote>);
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && (lines[i] ?? "").trim() && !/^\s*([-*+]|\d+\.)\s+|^#{1,3}\s|^>/.test(lines[i] ?? "")) {
      para.push(lines[i] ?? "");
      i++;
    }
    blocks.push(
      <p key={key}>
        {para.map((p, j) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static lines
          <Fragment key={j}>
            {j > 0 && <br />}
            {renderInline(p, `${key}-${j}-`)}
          </Fragment>
        ))}
      </p>,
    );
  }
  return <div className={className ? `markdown ${className}` : "markdown"}>{blocks}</div>;
}
